import { Component, ChangeDetectionStrategy, Input } from '@angular/core';
import { Observable, map } from 'rxjs';
import { ActivatedRoute, Params, Router } from '@angular/router';
import { TabNavigationItem } from '@app/navigation/navigation.model';
import { OpportunityPermissionsService } from '@app/opportunities/services/opportunity-permissions.service';
import { SubscriberBaseDirective } from '@app/shared/components/subscriber-base/subscriber-base.directive';
import { AuthService, SearchUrlService } from '@dg/shared-services';
import { TranslateService } from '@ngx-translate/core';
import { TabNavigationService } from '@app/navigation/components/tab-navigation/tab-navigation.service';

@Component({
  selector: 'dgx-search-tabs',
  templateUrl: './search-tabs.component.html',
  styleUrls: ['./search-tabs.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SearchTabsComponent extends SubscriberBaseDirective {
  public tabList$: Observable<TabNavigationItem[]>;
  private i18n: Record<string, string>;
  private tabList: TabNavigationItem[];
  @Input() showSearchTabs?: boolean = true;

  constructor(
    private translateService: TranslateService,
    private router: Router,
    private route: ActivatedRoute,
    private authService: AuthService,
    private tabNavigationService: TabNavigationService,
    private opportunityPermissionsService: OpportunityPermissionsService
  ) {
    super();
  }

  public ngOnInit(): void {
    this.i18n = this.translateService.instant([
      'Core_Learning',
      'Core_Skills',
      'Core_People',
      'Core_Groups',
      'Core_Opportunities',
    ]);

    this.tabList = this.tabNavigationService.formatTabs([
      {
        dgat: 'global-search-tabs-123',
        label: this.i18n.Core_Learning,
        routerLink: SearchUrlService.GLOBAL_SEARCH_BASE,
      },
      {
        dgat: 'global-search-tabs-372',
        label: this.i18n.Core_Skills,
        routerLink: SearchUrlService.SKILL_SEARCH_BASE,
      },
      {
        dgat: 'global-search-tabs-315',
        label: this.i18n.Core_People,
        routerLink: SearchUrlService.PEOPLE_SEARCH_BASE,
      },
      {
        dgat: 'global-search-tabs-954',
        label: this.i18n.Core_Groups,
        routerLink: SearchUrlService.GROUP_SEARCH_BASE,
        isAuthorized: !!this.authService.authUser.defaultOrgInfo,
      },
      {
        dgat: 'global-search-tabs-846',
        label: this.i18n.Core_Opportunities,
        routerLink: SearchUrlService.OPPORTUNITY_SEARCH_BASE,
        isAuthorized: this.opportunityPermissionsService.canViewOpportunities(),
      },
    ]);

    // Since we manually maintain the url for some of the search results pages
    // we need to keep the active tab params in sync with the browser URL.
    this.tabList$ = this.route.queryParams.pipe(
      this.takeUntilDestroyed(),
      map(this.setParams.bind(this))
    );
  }

  private setParams({ term }: Params): TabNavigationItem[] {
    return this.tabList.map((tab) => {
      const isActiveTab = this.router.url.includes(tab.routerLink);

      // Preserve the current params (including filters) for the active tab
      const queryParamsHandling = isActiveTab ? 'merge' : null;

      // Persist only the search term for other tabs
      const queryParams = isActiveTab ? null : { term };

      // Preserve the current url for the Opportunities tab since it contains the selected opportunity
      let specificTabOptions = {};
      if (tab.label === 'Opportunities' && isActiveTab) {
        specificTabOptions = {
          routerLink: [],
        };
      }

      return {
        ...tab,
        ...specificTabOptions,
        queryParamsHandling,
        queryParams,
      };
    });
  }
}
